import localforage from "localforage";

export const OFFLINE_DATA_KEY = "reduxPersist:offlineData";
export const OFFLINE_QUEUE_KEY = "reduxPersist:offline";

function isIndexedDbKey(key: string): boolean {
  return key === OFFLINE_DATA_KEY || key === OFFLINE_QUEUE_KEY;
}

function localStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function localStorageSet(key: string, value: string): void {
  window.localStorage.setItem(key, value);
}

function localStorageRemove(key: string): void {
  window.localStorage.removeItem(key);
}

function localStorageKeys(): string[] {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k) keys.push(k);
    }
    return keys;
  } catch {
    return [];
  }
}

function callbackify<T>(promise: Promise<T>, cb?: (err: any, result?: T) => void): Promise<T> {
  if (cb) {
    promise.then(
      (r) => cb(null, r),
      (e) => cb(e),
    );
  }
  return promise;
}

export const hybridStorage = {
  getAllKeys(cb?: (err: any, keys: string[]) => void): Promise<string[]> {
    const localKeys = localStorageKeys();
    const promise = localforage.keys().then((forageKeys) => {
      const merged = new Set([...localKeys, ...forageKeys]);
      return Array.from(merged);
    });
    return callbackify(promise, cb);
  },

  getItem(key: string, cb?: (err: any, value: string | null) => void): Promise<string | null> {
    if (isIndexedDbKey(key)) {
      const promise = localforage.getItem<string>(key).then((value) => {
        if (value !== null) return value;
        return localStorageGet(key);
      });
      return callbackify(promise, cb);
    }
    const value = localStorageGet(key);
    const promise = Promise.resolve(value);
    return callbackify(promise, cb);
  },

  setItem(key: string, value: string, cb?: (err: any) => void): Promise<void> {
    if (isIndexedDbKey(key)) {
      localStorageRemove(key);
      const promise = localforage.setItem(key, value).then(() => {});
      return callbackify(promise, cb);
    }
    localStorageSet(key, value);
    const promise = Promise.resolve();
    return callbackify(promise, cb);
  },

  removeItem(key: string, cb?: (err: any) => void): Promise<void> {
    if (isIndexedDbKey(key)) {
      const promise = localforage.removeItem(key);
      return callbackify(promise, cb);
    }
    localStorageRemove(key);
    const promise = Promise.resolve();
    return callbackify(promise, cb);
  },
};
